import React from 'react';
import { CodeTabs } from '../components/code/CodeTabs';
import { Link } from 'react-router-dom';
import { ArrowRight, Key, Shield, Zap, Globe } from 'lucide-react';

export const GettingStarted: React.FC = () => {
  const quickStartCode = [
    {
      language: 'javascript',
      code: `// 1. Install axios or use fetch
npm install axios

// 2. Authenticate and get access token
const auth = await fetch('https://agent.aku.africa/api/uaa/api/auth', {
  method: 'GET',
  headers: {
    'x-api-key': 'your-api-key',
    'x-client-id': 'your-client-id'
  }
});

const { accessToken } = await auth.json();

// 3. Use the token for API calls
const response = await fetch('https://agent.aku.africa/api/business/business/account/v2/balance', {
  method: 'GET',
  headers: {
    'Authorization': \`Bearer \${accessToken}\`
  }
});

const balance = await response.json();
console.log('Account Balance:', balance.accountBalance);`
    },
    {
      language: 'python',
      code: `# 1. Install requests
pip install requests

import requests

# 2. Authenticate and get access token
auth_response = requests.get(
    'https://agent.aku.africa/api/uaa/api/auth',
    headers={
        'x-api-key': 'your-api-key',
        'x-client-id': 'your-client-id'
    }
)

access_token = auth_response.json()['accessToken']

# 3. Use the token for API calls
balance_response = requests.get(
    'https://agent.aku.africa/api/business/business/account/v2/balance',
    headers={'Authorization': f'Bearer {access_token}'}
)

balance = balance_response.json()
print(f"Account Balance: {balance['accountBalance']}")`
    },
    {
      language: 'curl',
      code: `# 1. Authenticate and get access token
curl --request GET \\
  --url https://agent.aku.africa/api/uaa/api/auth \\
  --header 'x-api-key: your-api-key' \\
  --header 'x-client-id: your-client-id'

# Response: {"accessToken": "eyJhbGciOiJSUzI1NiJ9..."}

# 2. Use the token for API calls
curl --request GET \\
  --url https://agent.aku.africa/api/business/business/account/v2/balance \\
  --header 'Authorization: Bearer your-access-token'`
    }
  ];

  const steps = [
    {
      number: '01',
      icon: Key,
      title: 'Get API Credentials',
      description: 'Sign up for an Aku account and obtain your API key and client ID from the dashboard.',
      action: 'Get API Keys',
      href: 'https://merchant.akupay.africa/',
      external: true,
    },
    {
      number: '02',
      icon: Shield,
      title: 'Authenticate',
      description: 'Use your credentials to generate a short-lived access token via the authentication endpoint.',
      action: 'View Auth Docs',
      href: '/authentication',
      external: false,
    },
    {
      number: '03',
      icon: Zap,
      title: 'Make API Calls',
      description: 'Include the Bearer token in all requests. The token carries the authorities for the operations you can perform.',
      action: 'Explore Endpoints',
      href: '/collections',
      external: false,
    },
    {
      number: '04',
      icon: Globe,
      title: 'Go Live',
      description: 'Verify everything in the sandbox environment, then switch base URLs and credentials to go live.',
      action: 'See Environments',
      href: '#environments',
      external: false,
    },
  ];

  return (
    <div className="max-w-5xl mx-auto">

      {/* Page header */}
      <div className="mb-10 pb-8 border-b border-gray-100">
        <h1 className="text-2xl font-bold text-gray-900 mb-3">Getting Started</h1>
        <p className="text-base text-gray-500 leading-relaxed max-w-2xl">
          Get up and running with the Aku API in minutes — authenticate, make your first call, and start building.
        </p>
      </div>

      {/* Steps */}
      <div className="mb-12">
        <h2 className="text-base font-semibold text-gray-900 mb-5">Quick Start</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {steps.map((step, index) => (
            <div key={index} className="relative bg-white border border-gray-200 rounded-xl p-5 hover:border-gray-300 hover:shadow-sm transition-all duration-200">
              <div className="flex items-start gap-4">
                <div className="text-3xl font-black text-gray-100 leading-none select-none shrink-0 w-10 text-right">
                  {step.number}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="p-1.5 rounded-lg" style={{ background: 'rgba(88, 22, 137, 0.08)' }}>
                      <step.icon className="h-3.5 w-3.5 text-[#581689]" />
                    </div>
                    <h3 className="text-sm font-semibold text-gray-900">{step.title}</h3>
                  </div>
                  <p className="text-xs text-gray-500 leading-relaxed mb-3">{step.description}</p>
                  <Link
                    to={step.href}
                    target={step.external ? '_blank' : undefined}
                    className="inline-flex items-center text-xs font-semibold text-[#581689] hover:text-[#4a1275] transition-colors"
                  >
                    {step.action}
                    <ArrowRight className="ml-1 h-3 w-3" />
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Code example */}
      <div className="mb-12">
        <h2 className="text-base font-semibold text-gray-900 mb-2">Quick Start Example</h2>
        <p className="text-sm text-gray-500 mb-5">Authenticate and check your account balance:</p>
        <CodeTabs examples={quickStartCode} />
      </div>

      {/* Environments */}
      <div className="mb-12" id="environments">
        <h2 className="text-base font-semibold text-gray-900 mb-4">Environments</h2>
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Environment</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Auth Base URL</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">API Base URL</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Use</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                <tr className="hover:bg-gray-50 transition-colors">
                  <td className="px-5 py-3.5">
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-600">
                      <span className="h-1.5 w-1.5 rounded-full bg-amber-400" />
                      Sandbox
                    </span>
                  </td>
                  <td className="px-5 py-3.5">
                    <code className="text-xs font-mono text-gray-600 bg-gray-100 px-2 py-0.5 rounded">staging.agent.aku.africa</code>
                  </td>
                  <td className="px-5 py-3.5">
                    <code className="text-xs font-mono text-gray-600 bg-gray-100 px-2 py-0.5 rounded">staging.agent.aku.africa/api/paas</code>
                  </td>
                  <td className="px-5 py-3.5 text-xs text-gray-500">Development & Testing</td>
                </tr>
                <tr className="hover:bg-gray-50 transition-colors">
                  <td className="px-5 py-3.5">
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-600">
                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                      Production
                    </span>
                  </td>
                  <td className="px-5 py-3.5">
                    <code className="text-xs font-mono text-gray-600 bg-gray-100 px-2 py-0.5 rounded">agent.aku.africa</code>
                  </td>
                  <td className="px-5 py-3.5">
                    <code className="text-xs font-mono text-gray-600 bg-gray-100 px-2 py-0.5 rounded">agent.aku.africa/api/business</code>
                  </td>
                  <td className="px-5 py-3.5 text-xs text-gray-500">Live Transactions</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Security tips */}
      <div className="mb-12">
        <h2 className="text-base font-semibold text-gray-900 mb-4">Security Checklist</h2>
        <div className="space-y-2">
          {[
            { label: 'Never expose API keys', detail: 'Keep credentials server-side and out of client code or version control.' },
            { label: 'Always use HTTPS', detail: 'All API calls must go over HTTPS to ensure data is encrypted in transit.' },
            { label: 'Secure your tokens', detail: 'Store access tokens in memory or a secure store; refresh before expiry.' },
            { label: 'Handle errors gracefully', detail: 'Always check status codes and surface meaningful messages to users.' },
            { label: 'Respect rate limits', detail: 'Back off on 429 responses and avoid hammering endpoints in loops.' },
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-3 px-4 py-3 bg-white border border-gray-200 rounded-xl">
              <div className="mt-0.5 h-4 w-4 rounded-full flex items-center justify-center shrink-0" style={{ background: 'rgba(88,22,137,0.1)' }}>
                <div className="h-1.5 w-1.5 rounded-full bg-[#581689]" />
              </div>
              <p className="text-sm text-gray-600">
                <span className="font-medium text-gray-900">{item.label} —</span> {item.detail}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div className="rounded-2xl p-8 text-center" style={{ background: 'linear-gradient(135deg, #581689 0%, #7c22b8 100%)' }}>
        <h2 className="text-xl font-bold text-white mb-2">What's next?</h2>
        <p className="text-purple-200 text-sm mb-6 max-w-sm mx-auto">
          Explore the API sections that match your use case.
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          {[
            { label: 'Authentication', href: '/authentication' },
            { label: 'Collections', href: '/collections' },
            { label: 'Payouts', href: '/payouts' },
            { label: 'Account Management', href: '/account' },
          ].map(link => (
            <Link
              key={link.href}
              to={link.href}
              className="inline-flex items-center px-4 py-2 text-white text-sm font-medium rounded-lg border border-white/25 transition-colors hover:bg-white/15"
            >
              {link.label}
              <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
            </Link>
          ))}
        </div>
      </div>

    </div>
  );
};
